import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useNotify } from 'react-admin';
import { schoolApi } from '../../common/schoolApi';
import { QuestionView } from './QuestionView';

/**
 * The test's paper: its questions in order, with the key (the test's own
 * copy), marks and negative marks.
 *
 * Changes are made locally and saved in one call -- reordering twenty
 * questions should not be twenty requests. The API wants the COMPLETE order
 * whenever it gets one, so the whole list is always sent.
 *
 * Once a test is assigned the paper is frozen (the API refuses every change);
 * the panel says so and shows it read-only, with the way out: duplicate it.
 */

type Row = {
    id: number;
    question_order: number;
    source: string;
    question_id: number;
    marks: number;
    negative_marks: number;
    question_type: string;
    correct_answer: string;
    missing: boolean;
    content: any;
};

export const PaperPanel = ({ test, onChange }: { test: any; onChange: () => void }) => {
    const notify = useNotify();
    const [rows, setRows] = useState<Row[]>([]);
    const [removing, setRemoving] = useState<Record<number, boolean>>({});
    const [busy, setBusy] = useState(false);
    const editable = !!test.can_edit_questions;

    useEffect(() => {
        setRows(test.questions || []);
        setRemoving({});
    }, [test]);

    const original: Record<number, Row> = {};
    (test.questions || []).forEach((r: Row) => (original[r.id] = r));
    const dirty =
        rows.some((r, i) => !original[r.id] || original[r.id].question_order !== i + 1) ||
        rows.some(r => original[r.id] && (original[r.id].marks !== r.marks || original[r.id].negative_marks !== r.negative_marks));

    const move = (i: number, by: number) => {
        const j = i + by;
        if (j < 0 || j >= rows.length) return;
        const next = rows.slice();
        [next[i], next[j]] = [next[j], next[i]];
        setRows(next);
    };

    const set = (id: number, field: 'marks' | 'negative_marks', value: string) =>
        setRows(rows.map(r => (r.id === id ? { ...r, [field]: field === 'negative_marks' ? -Math.abs(Number(value)) : Number(value) } : r)));

    const save = async () => {
        setBusy(true);
        try {
            const changed = rows
                .filter(r => original[r.id].marks !== r.marks || original[r.id].negative_marks !== r.negative_marks)
                .map(r => ({ id: r.id, marks: r.marks, negative_marks: Math.abs(r.negative_marks) }));
            await schoolApi.post(`school-test/update_questions/${test.id}`, {
                questions: changed,
                order: rows.map(r => r.id),
            });
            notify('Paper saved.', { type: 'success' });
            onChange();
        } catch (e: any) {
            notify(e.message, { type: 'error' });
        } finally {
            setBusy(false);
        }
    };

    const remove = async () => {
        const ids = Object.keys(removing).filter(k => removing[Number(k)]).map(Number);
        if (ids.length === 0) return;
        if (!window.confirm(`Remove ${ids.length} question${ids.length === 1 ? '' : 's'} from this test?`)) return;
        setBusy(true);
        try {
            const r = await schoolApi.post(`school-test/remove_questions/${test.id}`, { ids });
            notify(
                r.result.test.status === 'draft' && test.status === 'ready'
                    ? 'Removed. The test is empty, so it is a draft again.'
                    : 'Removed.',
                { type: 'success' }
            );
            onChange();
        } catch (e: any) {
            notify(e.message, { type: 'error' });
        } finally {
            setBusy(false);
        }
    };

    const removeCount = Object.values(removing).filter(Boolean).length;

    return (
        <Card variant="outlined" sx={{ width: '100%', mb: 3 }}>
            <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
                    <Typography variant="subtitle1" sx={{ flex: 1 }}>
                        The paper &mdash; {rows.length} question{rows.length === 1 ? '' : 's'},{' '}
                        {rows.reduce((s, r) => s + Number(r.marks || 0), 0)} marks
                    </Typography>
                    {editable && (
                        <>
                            <Button color="error" disabled={busy || removeCount === 0} onClick={remove}>
                                Remove {removeCount || ''} selected
                            </Button>
                            <Button variant="contained" disabled={busy || !dirty} onClick={save}>
                                Save marks and order
                            </Button>
                        </>
                    )}
                </Box>

                {!editable && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        {test.locked
                            ? 'This test has been assigned, so its paper is frozen: students were marked against it. Duplicate it to make a changed version.'
                            : test.is_mine
                            ? 'An archived test cannot change. Set it back to draft to edit it.'
                            : 'This is a colleague’s test. You can read it and duplicate it, not change it.'}
                    </Alert>
                )}

                {rows.length === 0 ? (
                    <Alert severity="warning">No questions yet. Search the bank below and add some.</Alert>
                ) : (
                    <Box sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell width={40}>#</TableCell>
                                    <TableCell>Question</TableCell>
                                    <TableCell width={90}>Marks</TableCell>
                                    <TableCell width={110}>Minus if wrong</TableCell>
                                    {editable && <TableCell width={120} />}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((r, i) => (
                                    <TableRow key={r.id} sx={{ verticalAlign: 'top', opacity: removing[r.id] ? 0.4 : 1 }}>
                                        <TableCell>{i + 1}</TableCell>
                                        <TableCell>
                                            <QuestionView q={r.missing ? null : r.content} answer={r.correct_answer} compact />
                                        </TableCell>
                                        <TableCell>
                                            {editable ? (
                                                <TextField
                                                    size="small"
                                                    type="number"
                                                    value={r.marks}
                                                    inputProps={{ min: 1, max: 100 }}
                                                    onChange={e => set(r.id, 'marks', e.target.value)}
                                                />
                                            ) : (
                                                r.marks
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {editable ? (
                                                <TextField
                                                    size="small"
                                                    type="number"
                                                    value={Math.abs(r.negative_marks)}
                                                    inputProps={{ min: 0, max: 99, step: 0.5 }}
                                                    onChange={e => set(r.id, 'negative_marks', e.target.value)}
                                                />
                                            ) : (
                                                Math.abs(r.negative_marks)
                                            )}
                                        </TableCell>
                                        {editable && (
                                            <TableCell>
                                                <IconButton size="small" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Move up">
                                                    <ArrowUpwardIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton size="small" disabled={i === rows.length - 1} onClick={() => move(i, 1)} aria-label="Move down">
                                                    <ArrowDownwardIcon fontSize="small" />
                                                </IconButton>
                                                <Checkbox
                                                    size="small"
                                                    checked={!!removing[r.id]}
                                                    onChange={e => setRemoving({ ...removing, [r.id]: e.target.checked })}
                                                    inputProps={{ 'aria-label': 'Select to remove' }}
                                                />
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};
